import { API_SIMULATION_URL_V2, API_SIMULATION_URL, API_BASE_URL } from 'config'

import axios from 'axios'
import PropTypes from 'prop-types'
import { kea } from 'kea'
import { put, call } from 'redux-saga/effects'
import DataLogicV2 from 'containers/AppV2/logic'
import FiltersLogicV2 from 'containers/FiltersSectionV2/logic'
import ResultsLogicV2 from 'containers/ResultsV2/logic'
import {
  mapObjectToarray,
  validNumber,
  csObjectFromUnderscore,
  csObjectToUnderscore,
} from 'utils'

import pick from 'lodash/pick'
import mapKeys from 'lodash/mapKeys'
import omit from 'lodash/omit'
import cloneDeep from 'lodash/cloneDeep'

const PATCH_ENDPOINT = `${API_SIMULATION_URL_V2}/public/simulation/:code/`
const GET_FULL_TARIFF_ENDPOINT = `${API_BASE_URL}/public/price-list/:id/`
const INVOICING_ENDPOINT = `${API_SIMULATION_URL}/public/simulator/invoicing/`

const DEFAULT_VALUES = {
  consumerProfile: 'standard',
}

export default kea({
  connect: {
    props: [DataLogicV2, ['translations']],
    actions: [
      ResultsLogicV2,
      ['setSimulationResults'],
      FiltersLogicV2,
      ['setFilter'],
    ],
  },

  actions: () => ({
    editSimulation: (code) => ({ code }),
    resetForm: () => ({}),
    setFormValues: (selected) => selected,
    deleteFormValue: (key) => ({ key }),
    setEditError: (message) => ({ message }),

    fetchOptions: () => ({}),
    fetchOptionsError: (error) => ({ error }),
    setOptions: (options) => ({ options }),
    setPowerChoices: (options) => ({ options }),
    setTimeCycleChoices: (options) => ({ options }),
    setGasStagesChoices: (options) => ({ options }),

    fetchFullEleTariff: (id) => ({ id }),
    fetchFullEleTariffError: (error) => ({ error }),
    setFullEleTariff: (tariff) => ({ tariff }),

    fetchFullGasTariff: (id) => ({ id }),
    fetchFullGasTariffError: (error) => ({ error }),
    setFullGasTariff: (tariff) => ({ tariff }),
  }),
  reducers: ({ actions }) => ({
    formValues: [
      DEFAULT_VALUES,
      PropTypes.object,
      {
        [actions.setFormValues]: (state, payload) => ({ ...state, ...payload }),
        [actions.resetForm]: () => ({ ...DEFAULT_VALUES }),
        [actions.deleteFormValue]: (state, payload) => omit(state, payload.key),
      },
    ],
    options: [
      {},
      PropTypes.any,
      {
        [actions.setOptions]: (_, payload) => ({ ..._, ...payload.options }),
      },
    ],
    powerChoices: [
      [],
      PropTypes.any,
      {
        [actions.setPowerChoices]: (_, payload) => [...payload.options],
      },
    ],
    timeCycleChoices: [
      [],
      PropTypes.any,
      {
        [actions.setTimeCycleChoices]: (_, payload) => [...payload.options],
      },
    ],
    gasStagesChoices: [
      [],
      PropTypes.any,
      {
        [actions.setGasStagesChoices]: (_, payload) => [...payload.options],
      },
    ],
    loading: [
      true,
      PropTypes.bool,
      {
        [actions.editSimulation]: () => true,
        [actions.setFormValues]: () => false,
        [actions.resetForm]: () => false,
        [actions.deleteFormValue]: () => false,
        [actions.setEditError]: () => false,

        [actions.fetchFullEleTariff]: () => true,
        [actions.setFullEleTariff]: () => false,
        [actions.fetchFullEleTariffError]: () => false,

        [actions.fetchFullGasTariff]: () => true,
        [actions.setFullGasTariff]: () => false,
        [actions.fetchFullGasTariffError]: () => false,

        [actions.fetchOptions]: () => true,
        [actions.fetchFullTariff]: () => true,
        [actions.setFullTariff]: () => false,
        [actions.fetchFullTariffError]: () => false,
        [actions.setOptions]: () => false,
        [actions.setTimeCycleChoices]: () => false,
        [actions.setPowerChoices]: () => false,
        [actions.fetchOptionsError]: () => false,
      },
    ],
    error: [
      false,
      PropTypes.array,
      {
        [actions.fetchOptions]: () => false,
        [actions.setOptions]: () => false,
        [actions.setTimeCycleChoices]: () => false,
        [actions.setGasStagesChoices]: () => false,
        [actions.setPowerChoices]: () => false,
        [actions.fetchOptionsError]: (_, payload) => payload.error,
        [actions.fetchFullEleTariffError]: (_, payload) => payload.error,
        [actions.fetchFullGasTariffError]: (_, payload) => payload.error,
        [actions.setEditError]: (_, payload) => payload.message,
      },
    ],
    fullEleTariff: [
      {},
      PropTypes.any,
      {
        [actions.setFullEleTariff]: (_, payload) => ({ ...payload.tariff }),
      },
    ],
    fullGasTariff: [
      {},
      PropTypes.any,
      {
        [actions.setFullGasTariff]: (_, payload) => ({ ...payload.tariff }),
      },
    ],
  }),
  takeLatest: ({ actions, workers }) => ({
    [actions.editSimulation]: workers.editSimulation,
    [actions.fetchOptions]: workers.fetchSelectOptions,
    [actions.fetchFullEleTariff]: workers.fetchFullEleTariff,
    [actions.fetchFullGasTariff]: workers.fetchFullGasTariff,
  }),

  start: function*() {
    const {
      fetchOptions,
      fetchFullEleTariff,
      fetchFullGasTariff,
    } = this.actions
    const { electricityPriceList, options, gasPriceList } = this.props

    if (Object.keys(options).length === 0) {
      yield put(fetchOptions())
    }

    if (electricityPriceList && electricityPriceList.id) {
      yield put(fetchFullEleTariff(electricityPriceList.id))
    }

    if (gasPriceList && gasPriceList.id) {
      yield put(fetchFullGasTariff(gasPriceList.id))
    }
  },

  workers: {
    *editSimulation(action) {
      const { code } = action.payload
      const { setEditError, setSimulationResults, setFilter } = this.actions
      const {
        onClose,
        formValues,
        electricityPriceList,
        gasPriceList,
        prices,
      } = this.props

      let form = cloneDeep(formValues)

      delete form.electricityMonthlyBilling
      delete form.gasMonthlyBilling

      if (electricityPriceList) {
        form.electricityPrices = {
          cheia: validNumber(form.electricityCheia) || 0,
          ponta: validNumber(form.electricityPonta) || 0,
          super_vazio: prices.electricity.super_vazio,
          vazio: validNumber(form.electricityVazio) || 0,
          termo_fixo: validNumber(form.electricityTermoFixo) || 0,
        }
        delete form.electricityCheia
        delete form.electricityTermoFixo
        delete form.electricityPonta
        delete form.electricityVazio
      }

      if (gasPriceList) {
        form.gasPrices = {
          termo_energia: validNumber(form.gasTermoEnergia),
          termo_fixo: validNumber(form.gasTermoFixo),
          stage: form.stage,
        }
        form.gas_stage = form.stage

        delete form.gasTermoEnergia
        delete form.gasTermoFixo
        delete form.stage
      }

      if (form.consumerProfile === 'custom') {
        delete form.consumerProfile
        delete form.electricityAnnualBilling
        delete form.gasAnnualBilling

        if (electricityPriceList) {
          form.electricityMonthlyConsumption = {
            cheia: validNumber(form.electricityConsumeCheia) || 0,
            ponta: validNumber(form.electricityConsumePonta) || 0,
            vazio: validNumber(form.electricityConsumeVazio) || 0,
            super_vazio: 0,
          }
        }
      } else {
        delete form.gasMonthlyConsumption
        delete form.potenciaContratada
        delete form.cicloHorario
      }

      delete form.electricityConsumeCheia
      delete form.electricityConsumePonta
      delete form.electricityConsumeVazio

      let url
      if (code) {
        url = PATCH_ENDPOINT.replace(':code', code)
      } else {
        const queryParameters = new URLSearchParams(window.location.search)
        const urlCode = queryParameters.get('code')
        url = PATCH_ENDPOINT.replace(':code', urlCode)
      }

      try {
        // Omit invalid
        delete form.invalid
        const params = { ...csObjectToUnderscore(form) }
        const patch = yield call(axios.patch, url, params)
        const { data } = patch

        yield put(setFilter({ code: data.code }))
        yield put(setSimulationResults(csObjectFromUnderscore(data)))
        onClose && onClose()
        const urlParams = new URLSearchParams(window.location.search)
        urlParams.set('code', data.code)
        const newUrl = urlParams.toString()
        window.history.pushState('', '', '?' + newUrl + window.location.hash)
      } catch (error) {
        if (error.response.data && error.response.status === 400) {
          yield put(setEditError(error.response.data))
        }
      }
    },

    *fetchFullEleTariff(action) {
      const { id } = action.payload
      const { fetchFullEleTariffError, setFullEleTariff } = this.actions

      let url = GET_FULL_TARIFF_ENDPOINT.replace(':id', id)

      try {
        const tariff = yield call(axios.get, url)

        yield put(setFullEleTariff(csObjectFromUnderscore(tariff.data)))
      } catch (error) {
        yield put(fetchFullEleTariffError(error))
      }
    },

    *fetchFullGasTariff(action) {
      const { id } = action.payload
      const { fetchFullGasTariffError, setFullGasTariff } = this.actions

      let url = GET_FULL_TARIFF_ENDPOINT.replace(':id', id)

      try {
        const tariff = yield call(axios.get, url)

        yield put(setFullGasTariff(csObjectFromUnderscore(tariff.data)))
      } catch (error) {
        yield put(fetchFullGasTariffError(error))
      }
    },

    *fetchFullTariff(action) {
      const { id } = action.payload
      const { fetchFullTariffError, setFullTariff } = this.actions

      let url = GET_FULL_TARIFF_ENDPOINT.replace(':id', id)

      try {
        const tariff = yield call(axios.get, url)

        yield put(setFullTariff(csObjectFromUnderscore(tariff.data)))
      } catch (error) {
        yield put(fetchFullTariffError(error))
      }
    },

    /**
     * Fetches the data needed to populate the selects in the form
     */
    *fetchSelectOptions() {
      const {
        setOptions,
        setTimeCycleChoices,
        setPowerChoices,
        fetchOptionsError,
        setGasStagesChoices,
      } = this.actions
      const values = yield DataLogicV2.get('values')
      // Needed params for the Invoicing endpoint
      const params = pick(values, ['offer_type', 'municipality'])

      // Clean response data for frontend use
      const cleanData = (data) => {
        return {
          powerChoices: data.power_choices,
          timeCycleChoices: data.time_cycle_choices,
          gasStages: data.gas_stages,
        }
      }

      try {
        // The invoicing endpoint request
        const response = yield call(axios.get, INVOICING_ENDPOINT, { params })
        const data = cleanData(response.data)
        data.timeCycleChoices && delete data.timeCycleChoices['tetra-horário']

        // Replace . with , in powerchoices 😑
        data.powerChoices = mapKeys(data.powerChoices, (value, key) =>
          key.replace('.', ',')
        )

        data.powerChoices = mapObjectToarray(data.powerChoices)
        data.timeCycleChoices = mapObjectToarray(data.timeCycleChoices)
        data.gasStages = mapObjectToarray(data.gasStages)

        yield put(setOptions(data))
        yield put(setTimeCycleChoices(data.timeCycleChoices))
        yield put(setPowerChoices(data.powerChoices))
        yield put(setGasStagesChoices(data.gasStages))
      } catch (error) {
        yield put(fetchOptionsError(error))
      }
    },
  },
})



// WEBPACK FOOTER //
// ./src/components/EditDataModalV2/logic.js